import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
{ value: "single-family", label: "Single Family Estate" },
{ value: "multi-family", label: "Multi-Family Property" },
{ value: "penthouse", label: "Penthouse / Condo" },
{ value: "commercial", label: "Commercial Property" },
{ value: "new-construction", label: "New Construction" }];


export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    property_type: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, property_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Request submitted successfully", {
        description: "We'll be in touch within 24 hours."
      });
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        property_type: "",
        message: ""
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 overflow-hidden bg-[#0a0a0c]"
      data-testid="contact-section">

      {/* Background Gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#5e5ce6]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#5e5ce6]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#5e5ce6] font-medium">
                Get Started
              </span>
            </div>

            <h2
              className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
              data-testid="contact-headline">

              Request a Site Survey
            </h2>

            <p className="text-base text-white/50 leading-relaxed mb-12 max-w-md">
              Take the first step toward a truly private smart home. Our team will
              conduct a comprehensive assessment of your property.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#5e5ce6]" />
                </div>
                <div>
                  <p className="text-sm text-white/40">Location</p>
                  <p className="text-base text-white">Fairfield County, Southern Florida</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#5e5ce6]" />
                </div>
                <div>
                  <p className="text-sm text-white/40">Phone</p>
                  <p className="text-base text-white">+1 (917) 809-9279</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#5e5ce6]" />
                </div>
                <div>
                  <p className="text-sm text-white/40">Email</p>
                  <p className="text-base text-white">contact@fortressintegrations.com</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}>

            <form
              onSubmit={handleSubmit}
              className="glass-card rounded-3xl p-8 md:p-10"
              data-testid="contact-form">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                {/* First Name */}
                <div className="space-y-2">
                  <Label
                    htmlFor="first_name"
                    className="text-sm text-white/60">

                    First Name
                  </Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    placeholder="James"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl h-12"
                    data-testid="input-first-name" />

                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-sm text-white/60">
                    Last Name
                  </Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    placeholder="Sterling"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl h-12"
                    data-testid="input-last-name" />

                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-white/60">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="james@example.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl h-12"
                    data-testid="input-email" />

                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm text-white/60">
                    Phone (Optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl h-12"
                    data-testid="input-phone" />

                </div>
              </div>

              {/* Property Type */}
              <div className="space-y-2 mb-6">
                <Label htmlFor="property_type" className="text-sm text-white/60">
                  Property Type
                </Label>
                <Select
                  value={formData.property_type}
                  onValueChange={handleSelectChange}
                  required>

                  <SelectTrigger
                    className="bg-white/5 border-white/10 text-white focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl h-12"
                    data-testid="select-property-type">

                    <SelectValue placeholder="Select your property type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f11] border-white/10">
                    {propertyTypes.map((type) =>
                    <SelectItem
                      key={type.value}
                      value={type.value}
                      className="text-white/80 focus:bg-white/10 focus:text-white">

                        {type.label}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2 mb-8">
                <Label htmlFor="message" className="text-sm text-white/60">
                  Tell us about your project (Optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your current setup and what you're looking to achieve..."
                  rows={4}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#5e5ce6] focus:ring-1 focus:ring-[#5e5ce6] rounded-xl resize-none"
                  data-testid="input-message" />

              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 text-sm font-semibold shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-shadow duration-300 disabled:opacity-50"
                data-testid="submit-btn">

                {isSubmitting ?
                <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </> :

                <>
                    Request Survey
                    <Send className="w-4 h-4 ml-2" />
                  </>
                }
              </Button>

              <p className="text-xs text-white/30 text-center mt-6">
                Your information is encrypted and never shared.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>);

};